<template>
  <div class="material-status-tab">
    <!-- 디자인 요척 → 필요자재 가져오기 (2026-07-15, B안) —
         견적 없이 직접 만든 지시서를 조달 파이프라인에 태우는 진입로 -->
    <div class="tab-actions" v-if="canEdit">
      <el-button type="primary" plain size="small" @click="importFromYardage">
        디자인 요척에서 가져오기
      </el-button>
      <!-- [자재 추가] 버튼 제거 (2026-08-01 사이먼: 라인 추가와 중복) —
           추가는 아래 빠른 라인으로 단일화, 다이얼로그는 [수정] 전용
           (요척·로스율·자재마스터 연결·비고는 추가 후 수정에서) -->
      <span class="action-hint">
        견적 없이 만든 지시서는 디자인 요척 기준표(벌당 소요량×수량×로스율)로 필요자재를 생성하세요
      </span>
    </div>

    <!-- 빠른 라인 추가 (2026-08-01, 사이먼: "여러 자재를 추가 버튼 반복 클릭이
         힘들다") — 엔터/추가로 연속 입력. 유형·표기구분·단위·업체는 유지되어
         같은 카테고리 자재를 이어서 입력. 상세(요척·로스율·마스터 연결)는
         추가 후 [수정] 다이얼로그에서 -->
    <div v-if="canEdit" class="quick-add-row">
      <el-select v-model="quickForm.material_type" style="width: 86px">
        <el-option label="원자재" value="RAW" />
        <el-option label="부자재" value="ACC" />
      </el-select>
      <el-select v-model="quickForm.category" style="width: 92px">
        <el-option v-for="c in categoryOptions" :key="c.value" :label="c.label" :value="c.value" />
      </el-select>
      <el-input ref="quickNameRef" v-model="quickForm.item_name" placeholder="품명 *" style="width: 160px" @keyup.enter="quickAdd" />
      <el-input v-model="quickForm.usage_part" placeholder="사용부위" style="width: 90px" @keyup.enter="quickAdd" />
      <el-input v-model="quickForm.specification" placeholder="규격" style="width: 90px" @keyup.enter="quickAdd" />
      <el-input v-model="quickForm.color" placeholder="색상" style="width: 80px" @keyup.enter="quickAdd" />
      <el-input-number v-model="quickForm.required_quantity" :min="0" :controls="false" placeholder="필요량" style="width: 84px" @keyup.enter="quickAdd" />
      <el-input v-model="quickForm.unit" placeholder="단위" style="width: 60px" @keyup.enter="quickAdd" />
      <el-select v-model="quickForm.supplier" filterable clearable placeholder="업체" style="width: 110px">
        <el-option v-for="sp in suppliers" :key="sp.supplier_id" :label="sp.name" :value="sp.supplier_id" />
      </el-select>
      <el-button type="success" :loading="quickSaving" @click="quickAdd">추가 ↵</el-button>
    </div>

    <!-- 요약 -->
    <div class="summary-row" v-if="summary">
      <el-statistic title="전체" :value="summary.total" />
      <el-statistic title="확보완료" :value="summary.secured">
        <template #suffix>
          <el-tag type="success" size="small">{{ summary.secured_rate }}%</el-tag>
        </template>
      </el-statistic>
      <el-statistic title="진행중" :value="summary.in_progress" />
      <el-statistic title="미발주" :value="summary.not_ordered" />
    </div>

    <!-- 원자재 (디자인실) -->
    <div class="material-section" v-if="rawMaterials.length > 0">
      <div class="section-header">
        <h4>원자재 (디자인실)</h4>
        <el-button
          v-if="canEdit && hasUnordered(rawMaterials)"
          type="primary"
          size="small"
          @click="createOrder('raw')"
        >
          발주서 생성{{ selRaw.length ? ` (선택 ${selRaw.length}건)` : '' }}
        </el-button>
      </div>
      <div v-if="canEdit && hasUnordered(rawMaterials)" class="sel-hint">
        체크한 자재만 발주서에 담습니다 — 미선택 시 미발주 전체
      </div>
      <el-table :data="rawMaterials" border size="small" stripe
        @selection-change="(rows) => (selRaw = rows)">
        <!-- 선택 발주 (2026-07-28) — 발주서는 공급사 단위라 자재를 골라 담아야
             함 (부자재는 한 공급사에서 여러 자재를 묶어 발주). 미발주/일부발주만 선택 가능 -->
        <el-table-column v-if="canEdit" type="selection" width="40" :selectable="isOrderable" />
        <el-table-column prop="category_display" label="표기구분" width="80" align="center">
          <template #default="{ row }">
            <span :class="{ 'cat-missing': !row.category || row.category === 'OTHER' }">
              {{ row.category_display || '기타' }}
            </span>
          </template>
        </el-table-column>
        <el-table-column prop="item_name" label="품명" min-width="120" />
        <el-table-column prop="usage_part" label="사용부위" width="90">
          <template #default="{ row }">{{ row.usage_part || '-' }}</template>
        </el-table-column>
        <el-table-column prop="specification" label="규격" width="100" />
        <el-table-column prop="color" label="색상" width="80" />
        <el-table-column prop="required_quantity" label="필요량" width="90" align="right">
          <template #default="{ row }">
            {{ formatQty(row.required_quantity) }} {{ row.unit }}
          </template>
        </el-table-column>
        <el-table-column label="확보량" width="90" align="right">
          <template #default="{ row }">
            {{ formatQty(row.total_secured) }}
          </template>
        </el-table-column>
        <el-table-column label="확보율" width="100" align="center">
          <template #default="{ row }">
            <el-progress
              :percentage="row.secured_percentage"
              :status="row.is_fully_secured ? 'success' : ''"
              :stroke-width="14"
              text-inside
            />
          </template>
        </el-table-column>
        <el-table-column label="상태" width="100" align="center">
          <template #default="{ row }">
            <el-tag :type="getStatusType(row.procurement_status)" size="small">
              {{ row.procurement_status_display }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="source_type_display" label="조달방식" width="90" align="center" />
        <el-table-column prop="supplier_name" label="공급업체" width="100" />
        <!-- 행별 수정/삭제 (2026-07-15) -->
        <el-table-column v-if="canEdit" label="작업" width="100" align="center">
          <template #default="{ row }">
            <el-button link type="primary" size="small" @click="openEdit(row)">수정</el-button>
            <el-button link type="danger" size="small" @click="deleteMaterial(row)">삭제</el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- 부자재 (자재과) -->
    <div class="material-section" v-if="accMaterials.length > 0">
      <div class="section-header">
        <h4>부자재 (자재과)</h4>
        <el-button
          v-if="canEdit && hasUnordered(accMaterials)"
          type="primary"
          size="small"
          @click="createOrder('sub')"
        >
          발주서 생성{{ selAcc.length ? ` (선택 ${selAcc.length}건)` : '' }}
        </el-button>
      </div>
      <div v-if="canEdit && hasUnordered(accMaterials)" class="sel-hint">
        체크한 자재만 발주서에 담습니다 — 미선택 시 미발주 전체
      </div>
      <el-table :data="accMaterials" border size="small" stripe
        @selection-change="(rows) => (selAcc = rows)">
        <el-table-column v-if="canEdit" type="selection" width="40" :selectable="isOrderable" />
        <el-table-column prop="category_display" label="표기구분" width="80" align="center">
          <template #default="{ row }">
            <span :class="{ 'cat-missing': !row.category || row.category === 'OTHER' }">
              {{ row.category_display || '기타' }}
            </span>
          </template>
        </el-table-column>
        <el-table-column prop="item_name" label="품명" min-width="120" />
        <el-table-column prop="usage_part" label="사용부위" width="90">
          <template #default="{ row }">{{ row.usage_part || '-' }}</template>
        </el-table-column>
        <el-table-column prop="specification" label="규격" width="100" />
        <el-table-column prop="color" label="색상" width="80" />
        <el-table-column prop="required_quantity" label="필요량" width="90" align="right">
          <template #default="{ row }">
            {{ formatQty(row.required_quantity) }} {{ row.unit }}
          </template>
        </el-table-column>
        <el-table-column label="확보량" width="90" align="right">
          <template #default="{ row }">
            {{ formatQty(row.total_secured) }}
          </template>
        </el-table-column>
        <el-table-column label="확보율" width="100" align="center">
          <template #default="{ row }">
            <el-progress
              :percentage="row.secured_percentage"
              :status="row.is_fully_secured ? 'success' : ''"
              :stroke-width="14"
              text-inside
            />
          </template>
        </el-table-column>
        <el-table-column label="상태" width="100" align="center">
          <template #default="{ row }">
            <el-tag :type="getStatusType(row.procurement_status)" size="small">
              {{ row.procurement_status_display }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="source_type_display" label="조달방식" width="90" align="center" />
        <el-table-column prop="supplier_name" label="공급업체" width="100" />
        <!-- 행별 수정/삭제 (2026-07-15) -->
        <el-table-column v-if="canEdit" label="작업" width="100" align="center">
          <template #default="{ row }">
            <el-button link type="primary" size="small" @click="openEdit(row)">수정</el-button>
            <el-button link type="danger" size="small" @click="deleteMaterial(row)">삭제</el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- 자재 없음 -->
    <el-empty
      v-if="materials.length === 0 && !loading"
      description="등록된 필요자재가 없습니다."
    />

    <!-- 공장 전달 현황 -->
    <div class="material-section" v-if="transfers.length > 0">
      <div class="section-header">
        <h4>공장 전달 현황</h4>
      </div>
      <el-table :data="transfers" border size="small" stripe>
        <el-table-column prop="item_name" label="품명" min-width="120" />
        <el-table-column prop="from_warehouse_name" label="출발" width="100" />
        <el-table-column prop="to_warehouse_name" label="도착" width="100" />
        <el-table-column prop="quantity" label="수량" width="80" align="right" />
        <el-table-column prop="transfer_date" label="출고일" width="110" />
        <el-table-column prop="arrival_date" label="도착일" width="110" />
        <el-table-column label="상태" width="90" align="center">
          <template #default="{ row }">
            <el-tag :type="getTransferStatusType(row.status)" size="small">
              {{ row.status_display }}
            </el-tag>
          </template>
        </el-table-column>
      </el-table>
    </div>
    <!-- 필요자재 추가/수정 다이얼로그 (2026-07-15) -->
    <el-dialog
      v-model="dialogVisible"
      :title="editingId ? '필요자재 수정' : '필요자재 추가'"
      width="560px"
    >
      <el-form :model="matForm" label-width="90px">
        <el-form-item label="자재구분">
          <el-radio-group v-model="matForm.material_type">
            <el-radio-button value="RAW">원자재</el-radio-button>
            <el-radio-button value="ACC">부자재</el-radio-button>
          </el-radio-group>
        </el-form-item>
        <!-- 표기구분 (2026-07-30, 사이먼) — 배색/안감/심지/봉사/부자재/포장/기타.
             여기서 입력해야 생산지시서 가져오기 → PDF 블록 배치가 맞게 나옴 -->
        <el-form-item label="표기구분">
          <el-select v-model="matForm.category" style="width: 180px">
            <el-option
              v-for="c in categoryOptions"
              :key="c.value"
              :label="c.label"
              :value="c.value"
            />
          </el-select>
          <span class="dialog-hint">지시서 자재표 블록 (배색/안감/심지…)</span>
        </el-form-item>
        <el-form-item label="사용부위">
          <el-input
            v-model="matForm.usage_part"
            placeholder="예: 에리, 구찌, 미까시"
            style="width: 240px"
          />
        </el-form-item>
        <el-form-item label="품명" required>
          <!-- 자재 마스터 자동완성 (2026-07-15) — 발주 폼과 동일 부품.
               목록에 없으면 자유입력 그대로 (마스터 미연결) -->
          <el-autocomplete
            v-model="matForm.item_name"
            style="width: 100%"
            :fetch-suggestions="searchMaterialMaster"
            :trigger-on-focus="false"
            placeholder="품명 (마스터 검색)"
            @select="onMasterSelect"
            @input="() => { matForm.material = null }"
          >
            <template #default="{ item }">
              <span class="mm-code">{{ item.master.code }}</span>
              {{ item.master.name }}
              <span class="dialog-hint" v-if="item.master.specification"> · {{ item.master.specification }}</span>
            </template>
          </el-autocomplete>
          <div v-if="matForm.material" class="mm-linked">✓ 마스터 연결됨</div>
        </el-form-item>
        <el-form-item label="규격">
          <el-input v-model="matForm.specification" />
        </el-form-item>
        <el-form-item label="색상">
          <el-input v-model="matForm.color" />
        </el-form-item>
        <el-form-item label="단위">
          <el-input v-model="matForm.unit" style="width: 120px" />
        </el-form-item>
        <el-form-item label="요척(장당)">
          <el-input-number
            v-model="matForm.yardage"
            :min="0" :step="0.1" :precision="4" :controls="false"
            style="width: 130px"
          />
          <span style="margin: 0 4px 0 12px">로스율</span>
          <el-input-number
            v-model="matForm.loss_rate"
            :min="0" :max="100" :precision="2" :controls="false"
            style="width: 80px"
          />
          <span style="margin-left: 2px">%</span>
          <span class="dialog-hint">필요량을 비우면 요척 × 생산수량 × (1+로스%)로 자동 계산</span>
        </el-form-item>
        <el-form-item label="필요량">
          <el-input-number
            v-model="matForm.required_quantity"
            :min="0" :precision="3" :controls="false"
            style="width: 160px"
          />
        </el-form-item>
        <el-form-item label="조달방식">
          <el-select v-model="matForm.source_type" style="width: 180px">
            <el-option label="신규발주" value="PURCHASE" />
            <el-option label="LOT배분" value="LOT" />
            <el-option label="브랜드지급" value="BRAND" />
            <el-option label="공장현지조달" value="FACTORY_LOCAL" />
          </el-select>
        </el-form-item>
        <el-form-item label="공급업체">
          <!-- ⚠ Supplier PK 는 supplier_id (id 아님) -->
          <el-select
            v-model="matForm.supplier"
            filterable clearable placeholder="선택"
            style="width: 100%"
          >
            <el-option
              v-for="s in suppliers"
              :key="s.supplier_id"
              :label="s.name"
              :value="s.supplier_id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="비고">
          <el-input v-model="matForm.remarks" type="textarea" :rows="2" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">취소</el-button>
        <el-button type="primary" @click="saveMaterial">저장</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import axiosInstance from '@/api/axios'

const router = useRouter()

const props = defineProps({
  mainRequestId: { type: [Number, String], required: true },
  canEdit: { type: Boolean, default: false },
})

const loading = ref(false)
const materials = ref([])
const summary = ref(null)
const transfers = ref([])

const rawMaterials = computed(() =>
  materials.value.filter(m => m.material_type === 'RAW')
)
const accMaterials = computed(() =>
  materials.value.filter(m => m.material_type === 'ACC')
)

const fetchData = async () => {
  if (!props.mainRequestId) return
  loading.value = true
  try {
    const [matRes, sumRes, trfRes] = await Promise.all([
      axiosInstance.get(`production/main-requests/${props.mainRequestId}/materials/`),
      axiosInstance.get(`production/main-requests/${props.mainRequestId}/material-summary/`),
      axiosInstance.get(`production/main-requests/${props.mainRequestId}/material-transfers/`),
    ])
    materials.value = matRes.data
    summary.value = sumRes.data
    transfers.value = trfRes.data
  } catch (error) {
    console.error('Failed to fetch material status:', error)
  } finally {
    loading.value = false
  }
}

const formatQty = (val) => {
  if (val === null || val === undefined) return '-'
  const num = parseFloat(val)
  return num % 1 === 0 ? num.toFixed(0) : num.toFixed(2)
}

const getStatusType = (status) => {
  const types = {
    NOT_ORDERED: 'info',
    PARTIALLY_ORDERED: 'warning',
    FULLY_ORDERED: '',
    PARTIALLY_RECEIVED: 'warning',
    FULLY_RECEIVED: 'success',
    FACTORY_DELIVERED: 'success',
  }
  return types[status] || 'info'
}

const getTransferStatusType = (status) => {
  const types = {
    REQUESTED: 'info',
    IN_TRANSIT: 'warning',
    DELIVERED: 'success',
  }
  return types[status] || 'info'
}

const hasUnordered = (list) => {
  return list.some(m =>
    m.procurement_status === 'NOT_ORDERED' || m.procurement_status === 'PARTIALLY_ORDERED'
  )
}

// ===== 필요자재 수동 추가/수정/삭제 (2026-07-15) =====
const dialogVisible = ref(false)
const editingId = ref(null)
const matForm = ref({})
const suppliers = ref([])

// ── 빠른 라인 추가 (2026-08-01) — 연속 입력용 ──
const quickNameRef = ref(null)
const quickSaving = ref(false)
const quickForm = ref({ material_type: 'RAW', category: 'OTHER', item_name: '',
  usage_part: '', specification: '', color: '', required_quantity: null,
  unit: 'YD', supplier: null })

const quickAdd = async () => {
  if (!quickForm.value.item_name?.trim()) {
    ElMessage.warning('품명을 입력하세요.')
    return
  }
  quickSaving.value = true
  try {
    await axiosInstance.post('production/main-request-materials/', {
      main_request: props.mainRequestId,
      material_type: quickForm.value.material_type,
      category: quickForm.value.category,
      usage_part: quickForm.value.usage_part || '',
      item_name: quickForm.value.item_name.trim(),
      specification: quickForm.value.specification || '',
      color: quickForm.value.color || '',
      required_quantity: quickForm.value.required_quantity || 0,
      unit: quickForm.value.unit || 'YD',
      source_type: 'PURCHASE',
      supplier: quickForm.value.supplier || null,
      responsible_department: quickForm.value.material_type === 'RAW' ? '디자인실' : '자재과',
    })
    ElMessage.success(`"${quickForm.value.item_name.trim()}" 추가 — 이어서 입력하세요.`)
    // 유형·표기구분·단위·업체는 유지 (같은 카테고리 연속 입력), 나머지 초기화
    Object.assign(quickForm.value, {
      item_name: '', usage_part: '', specification: '', color: '',
      required_quantity: null,
    })
    quickNameRef.value?.focus?.()
    await fetchData()
  } catch (e) {
    ElMessage.error('추가 실패: ' + (e.response?.data?.detail || '입력값을 확인하세요.'))
  } finally {
    quickSaving.value = false
  }
}

// 표기구분 (2026-07-30) — WorkInstructionMaterial.Category 와 동일 코드
const categoryOptions = [
  { value: 'COLOR_BLOCK', label: '배색' },
  { value: 'LINING', label: '안감' },
  { value: 'INTERLINING', label: '심지' },
  { value: 'THREAD', label: '봉사' },
  { value: 'ACCESSORY', label: '부자재' },
  { value: 'PACKING', label: '포장' },
  { value: 'OTHER', label: '기타' },
]

// 자재 마스터 검색/연결 (2026-07-15) — MaterialOrderForm 과 동일 계약
const searchMaterialMaster = async (queryString, cb) => {
  if (!queryString || queryString.length < 1) { cb([]); return }
  try {
    const res = await axiosInstance.get('/material-masters/search/', {
      params: { q: queryString },
    })
    cb((res.data || []).map(m => ({ value: m.name, master: m })))
  } catch (err) {
    console.error('자재 마스터 검색 실패:', err)
    cb([])
  }
}

// 마스터 품목군 → 지시서 표기구분 자동 매핑 (2026-07-30, 폼 검토) —
// 검색 응답에 이미 category(품목군)가 오는데 안 쓰고 있었음 (이중 입력 제거).
// 원단·패딩·다운은 배색/주원단 여부를 품목군만으로 알 수 없어 자동 지정 제외.
const CATEGORY_FROM_MASTER = {
  '안감': 'LINING', '심지': 'INTERLINING', '봉사': 'THREAD',
  '옷걸이': 'PACKING', '폴리백': 'PACKING', '박스': 'PACKING',
  '지퍼': 'ACCESSORY', '돗도': 'ACCESSORY', '히든돗도': 'ACCESSORY',
  '금속': 'ACCESSORY', '단추': 'ACCESSORY', '장식': 'ACCESSORY',
  '테이프': 'ACCESSORY', '밴드': 'ACCESSORY', '스냅': 'ACCESSORY',
  '라벨/택': 'ACCESSORY',
}

const onMasterSelect = (item) => {
  const m = item.master
  matForm.value.material = m.id
  matForm.value.item_name = m.name
  matForm.value.material_type = m.material_type || matForm.value.material_type
  if (!matForm.value.specification) matForm.value.specification = m.specification || ''
  if (m.unit) matForm.value.unit = m.unit
  // 표기구분 자동 채움 — 사용자가 이미 고른 값(기타 외)은 덮지 않음
  if (matForm.value.category === 'OTHER' && CATEGORY_FROM_MASTER[m.category]) {
    matForm.value.category = CATEGORY_FROM_MASTER[m.category]
  }
  if (m.default_supplier && !matForm.value.supplier) {
    matForm.value.supplier = m.default_supplier
    if (!suppliers.value.length) fetchSuppliers()
  }
}

const fetchSuppliers = async () => {
  try {
    const res = await axiosInstance.get('/suppliers/')
    suppliers.value = Array.isArray(res.data.results)
      ? res.data.results : (res.data || [])
  } catch (err) {
    console.error('공급사 목록 조회 실패:', err)
  }
}

const openEdit = (row) => {
  editingId.value = row.id
  matForm.value = {
    material_type: row.material_type,
    category: row.category || 'OTHER',
    usage_part: row.usage_part || '',
    item_name: row.item_name,
    specification: row.specification || '',
    color: row.color || '',
    unit: row.unit || 'YD',
    yardage: row.yardage != null ? parseFloat(row.yardage) : null,
    loss_rate: row.loss_rate != null ? parseFloat(row.loss_rate) : null,
    required_quantity:
      row.required_quantity != null ? parseFloat(row.required_quantity) : null,
    source_type: row.source_type || 'PURCHASE',
    supplier: row.supplier || null,
    remarks: row.remarks || '',
    material: row.material || null,
  }
  dialogVisible.value = true
  if (!suppliers.value.length) fetchSuppliers()
}

const extractError = (err, fallback) => {
  const data = err.response?.data
  if (!data) return fallback
  const first = Array.isArray(data)
    ? data[0]
    : (data.detail ?? Object.values(data)[0])
  const text = Array.isArray(first) ? first[0] : first
  return typeof text === 'string' ? text : fallback
}

const saveMaterial = async () => {
  if (!matForm.value.item_name) {
    ElMessage.warning('품명을 입력하세요.')
    return
  }
  const payload = {
    ...matForm.value,
    required_quantity: matForm.value.required_quantity || 0,
    // 견적/요척 경로와 동일 규칙
    responsible_department:
      matForm.value.material_type === 'RAW' ? '디자인실' : '자재과',
  }
  try {
    if (editingId.value) {
      await axiosInstance.patch(
        `production/main-request-materials/${editingId.value}/`, payload)
      ElMessage.success('수정했습니다.')
    } else {
      await axiosInstance.post(
        `production/main-requests/${props.mainRequestId}/materials/`, payload)
      ElMessage.success('추가했습니다.')
    }
    dialogVisible.value = false
    await fetchData()
  } catch (err) {
    ElMessage.error(extractError(err, '저장에 실패했습니다.'))
  }
}

const deleteMaterial = async (row) => {
  try {
    await ElMessageBox.confirm(
      `'${row.item_name}' 자재를 삭제할까요?`,
      '필요자재 삭제',
      { type: 'warning', confirmButtonText: '삭제', cancelButtonText: '취소' }
    )
  } catch {
    return
  }
  try {
    await axiosInstance.delete(`production/main-request-materials/${row.id}/`)
    ElMessage.success('삭제했습니다.')
    await fetchData()
  } catch (err) {
    // 발주 항목 연결 자재는 서버가 삭제 거부
    ElMessage.error(extractError(err, '삭제에 실패했습니다.'))
  }
}

// 디자인 요척 기준표 → 필요자재 생성 (2026-07-15)
const importFromYardage = async () => {
  try {
    await ElMessageBox.confirm(
      '디자인 요척 기준표(벌당 소요량 × 수량 × 로스율)로 필요자재를 생성합니다. '
      + '이미 있는 품명/색상/규격은 건너뜁니다.',
      '디자인 요척에서 가져오기',
      { confirmButtonText: '가져오기', cancelButtonText: '취소', type: 'info' }
    )
  } catch {
    return
  }
  try {
    const res = await axiosInstance.post(
      `production/main-requests/${props.mainRequestId}/materials/from-design-yardage/`
    )
    const { created = [], skipped = [] } = res.data
    let msg = `필요자재 ${created.length}건 생성`
    if (skipped.length) msg += `, ${skipped.length}건 건너뜀 (중복/소요량 0)`
    ElMessage.success(msg)
    await fetchData()
  } catch (err) {
    ElMessage.error(err.response?.data?.error || '요척 가져오기에 실패했습니다.')
  }
}

// ===== 선택 자재 발주 (2026-07-28) =====
// 발주서는 공급사 단위라 자재를 골라 담는다 (부자재는 한 공급사에서 여러
// 자재를 묶어 발주하는 관행 — 사이먼 확정). 체크된 자재가 있으면 그것만
// materials 파라미터로 넘기고, 없으면 기존처럼 미발주 전체 채우기.
// 공급사가 섞여 있으면 진행 전 확인 (발주서 하나 = 공급사 하나).
const selRaw = ref([])
const selAcc = ref([])

const isOrderable = (row) =>
  row.procurement_status === 'NOT_ORDERED' || row.procurement_status === 'PARTIALLY_ORDERED'

const createOrder = async (materialType) => {
  const selected = materialType === 'raw' ? selRaw.value : selAcc.value
  const query = {
    main_request: props.mainRequestId,
    material_type: materialType,
  }
  if (selected.length) {
    const suppliers = [...new Set(selected.map(r => r.supplier_name).filter(Boolean))]
    if (suppliers.length > 1) {
      try {
        await ElMessageBox.confirm(
          `선택한 자재의 공급업체가 서로 다릅니다 (${suppliers.join(', ')}). ` +
          '발주서는 공급사 하나로 저장됩니다 — 계속하시겠습니까?',
          '공급업체 확인',
        )
      } catch { return }
    }
    query.materials = selected.map(r => r.id).join(',')
  }
  router.push({ path: '/material-orders/new', query })
}

watch(() => props.mainRequestId, fetchData, { immediate: true })

onMounted(() => {
  fetchData()
  // 빠른 라인 추가의 업체 셀렉트용 — 편집 권한자만 (2026-08-01)
  if (props.canEdit) fetchSuppliers()
})
</script>

<style scoped>
.quick-add-row {
  display: flex; gap: 4px; align-items: center; flex-wrap: wrap;
  padding: 6px 8px; margin-bottom: 10px;
  border: 1px dashed var(--el-color-primary, #1f3a5f); border-radius: 6px;
  background: #fafbfc;
}

.material-status-tab {
  padding: 8px 0;
}
.tab-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}
.action-hint {
  font-size: 12px;
  color: #909399;
}
.dialog-hint {
  font-size: 12px;
  color: #909399;
  margin-left: 10px;
}
.summary-row {
  display: flex;
  gap: 32px;
  margin-bottom: 20px;
  padding: 16px;
  background: #f5f7fa;
  border-radius: 8px;
}
.material-section {
  margin-bottom: 24px;
}
.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}
.sel-hint {
  font-size: 12px;
  color: #909399;
}
.section-header h4 {
  margin: 0;
  font-size: 14px;
  color: #303133;
}
/* 자재 마스터 자동완성 (2026-07-15) — MaterialOrderForm 과 동일 스타일 */
.mm-code {
  font-family: monospace;
  font-size: 12px;
  color: #409eff;
  margin-right: 6px;
}
.mm-linked {
  font-size: 11px;
  color: #67c23a;
  line-height: 1.4;
}
/* 표기구분 미입력(기타) 표시 (2026-07-30) — 지시서 PDF 블록이 '기타'로
   몰리지 않게 입력을 유도 */
.cat-missing {
  color: #e6a23c;
}
</style>
